
import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist";
import axios from "axios";
import Swal from "sweetalert2";
import { GenerateContent } from "../apiconstants/constants";
import { TOTAL_RESUME_EXTRACTION_PROMPT } from "./totalresumeextraction";
/**
 * PUBLIC FUNCTION (this is the only thing EditCandidate will call)
 */

// export const extractResumeDetails = async ({
//   file,
//   token,
//   jobResponsibilities = "",
// }) => {
//   if (!file) throw new Error("No resume file provided");

//   const resumeText = await extractTextFromFile(file);
//   const details = await extractDetails(resumeText, token, jobResponsibilities);

//   return {
//     resumeText,
//     ...details,
//   };
// };


export const extractResumeDetails = async ({ file, token }) => {
  if (!file) {
    throw new Error("Resume file is required");
  }

  // 1️⃣ Extract raw text from file
  const resumeText = await extractTextFromFile(file);

  // 2️⃣ Build prompt
  const prompt = TOTAL_RESUME_EXTRACTION_PROMPT.replace(
    "{{RESUME_TEXT}}",
    resumeText
  );

  // 3️⃣ Call AI
  const response = await axios.post(
    GenerateContent.APIURL,
    { prompt },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  // 4️⃣ Parse response safely
  const raw =
    response.data?.response?.candidates?.[0]?.content?.parts?.[0]?.text || "";

  const cleaned = raw.replace(/```json|```/g, "").trim();

  return JSON.parse(cleaned);
};

/* ------------------ INTERNAL HELPERS ------------------ */

const extractTextFromFile = async (file) => {
  if (file.type === "application/pdf") {
    return extractTextFromPDF(file);
  }

  if (
    file.type ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return extractTextFromDOCX(file);
  }

  throw new Error("Unsupported file type");
};

const extractTextFromPDF = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  let extractedText = "";

  for (let i = 0; i < pdf.numPages; i++) {
    const page = await pdf.getPage(i + 1);
    const textContent = await page.getTextContent();
    extractedText += textContent.items.map((item) => item.str).join(" ") + " ";
  }

  return extractedText;
};

const extractTextFromDOCX = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
};

const extractDetails = async (text, token, jobResponsibilities) => {
  try {
    // 1️⃣ Extract certifications
    const certResponse = await axios.post(
      GenerateContent.APIURL,
      {
        prompt: `Extract all certifications mentioned in this resume text.
Return only a comma-separated list:\n\n${text}`,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const certText =
      certResponse.data?.response?.candidates?.[0]?.content?.parts?.[0]?.text ||
      "";

    const certifications = certText
      ? certText.split(",").map((c) => c.trim())
      : [];

    // 2️⃣ Extract skills
    const skills = await extractSkills(text, token);

    // 3️⃣ Extract profile details
    const profile = await extractProfileDetails(
      text,
      token,
      jobResponsibilities
    );

    return {
      skills,
      certifications,
      ...profile,
    };
  } catch (error) {
    if (error.response?.status === 401) {
      Swal.fire("Session expired", "Please login again", "error");
    }
    throw error;
  }
};

const extractSkills = async (text, token) => {
  const response = await axios.post(
    GenerateContent.APIURL,
    {
      prompt: `Extract technical skills as a comma-separated list:\n\n${text}`,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  const skillsText =
    response.data?.response?.candidates?.[0]?.content?.parts?.[0]?.text || "";

  return skillsText ? skillsText.split(",").map((s) => s.trim()) : [];
};

const extractProfileDetails = async (resumeText, token, jobResponsibilities) => {
  const response = await axios.post(
    GenerateContent.APIURL,
    {
      prompt: `
Return JSON only with:
name, email, phoneNumber, profilesummary, experience, address, linkedin

Resume:
${resumeText}

Job Description:
${jobResponsibilities}
      `,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  const raw =
    response.data?.response?.candidates?.[0]?.content?.parts?.[0]?.text || "";

  try {
    return JSON.parse(raw.replace(/```json|```/g, "").trim());
  } catch {
    return {};
  }
};

